import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/quickwash/Navbar";
import Footer from "@/components/quickwash/Footer";
import api from "@/lib/api";
import { Truck, CheckCircle2 } from "lucide-react";

const services = ["Laundry (Wash & Fold)", "Wash & Iron", "Dry Cleaning", "Steam Iron", "Shoe Cleaning", "Blanket", "Curtain"];

const Booking = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", address: "", pickup_date: "", pickup_time: "10:00",
    service_type: "Laundry (Wash & Fold)", instructions: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", form);
      setSubmitted(data);
      toast.success("Pickup booked! We'll call to confirm shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit booking");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex flex-col">
        <Navbar />
        <div className="qw-container py-24 flex-1">
          <div className="max-w-lg mx-auto qw-card p-10 text-center" data-testid="booking-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-5"><CheckCircle2 size={30}/></div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Pickup Booked!</h2>
            <p className="text-slate-600 mt-2">Booking ID: <span className="font-mono text-slate-900">{submitted.id.slice(0, 8)}</span></p>
            <p className="text-sm text-slate-500 mt-4">We'll call {submitted.mobile} to confirm your pickup on {submitted.pickup_date} at {submitted.pickup_time}.</p>
            <div className="mt-8 flex gap-3 justify-center">
              <button onClick={() => navigate("/track")} className="qw-btn-outline" data-testid="booking-track-btn">Track Order</button>
              <button onClick={() => navigate("/")} className="qw-btn-primary" data-testid="booking-home-btn">Back to Home</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen flex flex-col">
      <Navbar />
      <div className="qw-container py-14 lg:py-20 grid lg:grid-cols-5 gap-10 flex-1">
        <div className="lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">Book Pickup</div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>Schedule a free doorstep pickup</h1>
          <p className="text-slate-600 leading-relaxed">Fill in your details and preferred time. We'll call to confirm and pick up your clothes from your doorstep — absolutely free across Rohtak.</p>
          <img src="https://images.pexels.com/photos/7363192/pexels-photo-7363192.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Pickup" className="rounded-2xl mt-8 shadow-lg h-64 w-full object-cover"/>
        </div>
        <form onSubmit={submit} className="lg:col-span-3 qw-card p-6 md:p-10 space-y-5" data-testid="booking-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div><label className="qw-label">Full Name *</label><input required className="qw-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="book-name"/></div>
            <div><label className="qw-label">Mobile Number *</label><input required maxLength={10} className="qw-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} data-testid="book-mobile"/></div>
          </div>
          <div><label className="qw-label">Address *</label><textarea required rows={2} className="qw-input" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="book-address"/></div>
          <div className="grid md:grid-cols-3 gap-5">
            <div><label className="qw-label">Pickup Date *</label><input required type="date" min={new Date().toISOString().slice(0, 10)} className="qw-input" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} data-testid="book-date"/></div>
            <div><label className="qw-label">Time *</label><input required type="time" className="qw-input" value={form.pickup_time} onChange={(e) => set("pickup_time", e.target.value)} data-testid="book-time"/></div>
            <div><label className="qw-label">Service *</label>
              <select className="qw-input" value={form.service_type} onChange={(e) => set("service_type", e.target.value)} data-testid="book-service">
                {services.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div><label className="qw-label">Special Instructions</label><textarea rows={3} className="qw-input" value={form.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="book-instructions"/></div>
          <button disabled={loading} type="submit" className="qw-btn-primary w-full" data-testid="book-submit"><Truck size={18}/>{loading ? "Booking…" : "Confirm Pickup"}</button>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default Booking;
